import React, { useState } from 'react';
import { getOrgConfig } from '../../data/orgConfigs';
import {
  CONSENT_NO_REAL, CONSENT_RECORD, CONSENT_TIME_CALIBRATION, CONSENT_TIME_MAIN, CONSENT_VOLUNTARY,
  CONSENT_WHAT_CALIBRATION, CONSENT_WHAT_MAIN, CONTACT_LINE, IRB_PROTOCOL, RESEARCH_CONTACT,
} from '../../data/studyText';
import './ConsentForm.css';

interface ConsentFormProps {
  onConsent: () => void;
  /** Active arm from GET /arm: 'calibration' | 'main' (verification study) | anything else = legacy text. */
  arm?: string | null;
  mainStudy?: string;
}

const ConsentForm: React.FC<ConsentFormProps> = ({ onConsent, arm, mainStudy }) => {
  const variant: 'calibration' | 'main' | 'legacy' =
    arm === 'calibration' ? 'calibration' : (arm === 'main' && mainStudy === 'verification') ? 'main' : 'legacy';
  const [agreed, setAgreed] = useState(false);
  const [declined, setDeclined] = useState(false);
  const isSona = getOrgConfig().recruitment === 'sona';
  const sonaId = localStorage.getItem('sonaId');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (agreed) {
      onConsent();
    }
  };

  const handleDecline = () => {
    setDeclined(true);
  };

  if (declined) {
    return (
      <div className="consent-container">
        <div className="consent-form">
          <h1>Thank You</h1>
          <p>You chose not to participate. No study record has been created.</p>
          <p>You may close this browser tab.</p>
        </div>
      </div>
    );
  }

  // Without a code the run cannot earn credit, so stop before the student spends the time.
  if (isSona && !sonaId) {
    return (
      <div className="consent-container">
        <div className="consent-form">
          <h1>Please Use Your SONA Link</h1>
          <p>
            We could not find your SONA code, so credit could not be recorded for this session.
            Please return to SONA and open the study from the link provided there.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="consent-container">
      <div className="consent-form">
        <h1>Research Consent Form</h1>
        {variant === 'calibration' && <h2>University Message Study</h2>}
        {variant === 'main' && <h2>University Message Study</h2>}
        {variant === 'legacy' && <h2>Email and Device Security Decision-Making Study</h2>}
        
        <div className="consent-content">
          <h3>Study Information</h3>
          {variant === 'legacy' ? (
            <p>
              You are being invited to participate in a research study about how people make
              decisions when evaluating workplace communications and technology scenarios.
            </p>
          ) : (
            <p>
              You are being invited to participate in a research study about how students read
              and respond to university email messages.
            </p>
          )}

          <h3>What You Will Do</h3>
          {variant === 'calibration' && (
            <>
              <p>{CONSENT_WHAT_CALIBRATION}</p>
              <p><strong>{CONSENT_TIME_CALIBRATION}</strong></p>
              <p>{CONSENT_NO_REAL}</p>
            </>
          )}
          {variant === 'main' && (
            <>
              <p>{CONSENT_WHAT_MAIN}</p>
              <p><strong>{CONSENT_TIME_MAIN}</strong></p>
              <p>{CONSENT_NO_REAL}</p>
            </>
          )}
          {variant === 'legacy' && (
            <>
              <ul>
                <li>Complete a brief work-background survey (2-3 minutes)</li>
                <li>Evaluate a series of emails and decide how to respond (10-15 minutes)</li>
                <li>Review technology scenarios and make decisions (10-15 minutes)</li>
                <li>Complete a brief reset activity between the two main tasks</li>
                <li>Answer questions about your experience (5 minutes)</li>
              </ul>
              <p><strong>Total time: Approximately 30-40 minutes</strong></p>
            </>
          )}

          <h3>Risks</h3>
          {variant === 'legacy' ? (
            <p>
              This study involves minimal risk. You may experience mild frustration or stress
              when evaluating uncertain or potentially suspicious situations. You may take the
              time you need for each decision.
            </p>
          ) : (
            <p>
              This study involves minimal risk. All messages are simulated; some are designed to
              look like requests you should not follow. You may take the time you need for each
              decision.
            </p>
          )}

          <h3>Benefits</h3>
          <p>
            Your participation will help improve understanding of decision-making processes in workplace contexts.
          </p>

          {variant === 'legacy' ? (
            <>
              <h3>Confidentiality</h3>
              <p>
                Your responses will be kept confidential and used only for research purposes.{' '}
                The study records survey responses, decisions, timing and mouse-movement data, and technical metadata such as IP address, browser information, language, and referrer.{' '}
                {isSona
                  ? 'Your SONA code is also stored with the study record so research credit can be assigned.'
                  : 'If you participate through Prolific, your Prolific ID is also stored with the study record so compensation can be administered.'}
              </p>
            </>
          ) : (
            <>
              <h3>What We Record</h3>
              <p>{CONSENT_RECORD} Your responses are kept confidential and used only for research purposes.</p>
            </>
          )}

          <h3>Voluntary Participation &amp; Compensation</h3>
          {variant !== 'legacy' ? (
            <p>{CONSENT_VOLUNTARY}</p>
          ) : isSona ? (
            <>
              <p>
                Your participation is voluntary and you may withdraw at any point. Research
                credit is recorded through SONA when you finish the study and are returned to
                SONA automatically.
              </p>
              <p>
                Your SONA code is used only to record credit and for study administration.
                We do not ask for your name, postal address, or personal account passwords.
              </p>
            </>
          ) : (
            <>
              <p>
                Your participation is voluntary. You may withdraw at any point; however,
                compensation is provided only upon completion of the study. To receive payment
                you must finish the study and follow the completion link back to Prolific.
              </p>
              <p>
                Your Prolific ID is used for payment reconciliation and study administration.
                We do not ask for your name, postal address, or personal account passwords.
              </p>
            </>
          )}

          {/* Contact and IRB details are shown by default (advisor §7.2); no toggle. */}
          <div className="full-consent" data-testid="consent-details">
            <h3>Additional Information</h3>
            <p>
              <strong>Data Collection:</strong>{' '}
              {variant === 'legacy'
                ? 'We will collect your responses to survey questions, your decisions in email and device-security scenarios, timing and mouse-movement patterns, and the technical metadata described above.'
                : 'We will collect your answers to the questions, your decisions and interactions in the mock inbox, timing, and the technical metadata described above.'}
            </p>
            <p>
              <strong>Data Storage:</strong> Data will be stored on secure servers and retained
              for up to 5 years for research purposes.
            </p>
            <p data-testid="consent-contact">
              <strong>Contact:</strong>{' '}
              {RESEARCH_CONTACT
                ? <>If you have questions about this research, or want your answers deleted, please contact {RESEARCH_CONTACT}.</>
                : <>If you have questions about this research, or want your answers deleted, please contact {CONTACT_LINE}.</>}
            </p>
            {IRB_PROTOCOL && (
              <p data-testid="consent-irb">
                <strong>IRB Protocol:</strong> {IRB_PROTOCOL}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="consent-actions">
          <div className="consent-checkbox">
            <input
              type="checkbox"
              id="consent-agree"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            <label htmlFor="consent-agree">
              I have read and understood the information above. I voluntarily agree to 
              participate in this research study.
            </label>
          </div>

          <div className="consent-buttons">
            <button 
              type="button" 
              onClick={handleDecline}
              className="btn btn-secondary"
            >
              Decline to Participate
            </button>
            <button 
              type="submit" 
              disabled={!agreed}
              className="btn btn-primary"
            >
              I Agree - Begin Study
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConsentForm;
